import { useEffect, useState } from "react";

/** Messaggio postMessage inviato da TechyWeb (parent) alle app in iframe. */
export const TL4K_ALL_CAPS_MESSAGE = "TL4K_ALL_CAPS";

/**
 * URL parameter usato all'apertura per lo stato iniziale (prima del postMessage).
 * TechyWeb imposta `allCaps=1` quando apre un'app con il maiuscolo attivo.
 */
const ALL_CAPS_URL_PARAM = "allCaps";

/**
 * Stato "tutto maiuscolo" sincronizzato con l'embedder (TechyWeb).
 *
 * Quando l'app gira DENTRO TechyWeb (iframe):
 * - al mount legge `?allCaps=1` dall'URL (stato iniziale);
 * - ascolta i postMessage `TL4K_ALL_CAPS` inviati dal parent quando l'insegnante
 *   (o la shell) attiva/disattiva il maiuscolo.
 *
 * Quando l'app gira standalone il comportamento è identico a `useState`:
 * la UI locale (toggle in TL4KAppHeader) continua a funzionare.
 *
 * @param initial Valore di default quando non c'è embedding (es. `useState(true)` per Disegna).
 */
export function useEmbeddedAllCaps(initial = false): [boolean, (next: boolean | ((prev: boolean) => boolean)) => void] {
  const [allCaps, setAllCaps] = useState<boolean>(() => {
    if (typeof window === "undefined") return initial;
    try {
      const param = new URLSearchParams(window.location.search).get(ALL_CAPS_URL_PARAM);
      if (param === "1" || param === "true") return true;
      if (param === "0" || param === "false") return false;
    } catch {
      // URL malformato: usa il default
    }
    return initial;
  });

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      // Accetta solo messaggi dall'embedder (TechyWeb): quando l'app gira in
      // iframe `event.source` è il contentWindow del parent; standalone nessun
      // messaggio esterno deve poter cambiare la UI.
      if (event.source !== window.parent) return;
      const data = event.data as { type?: string; enabled?: boolean } | undefined;
      if (data?.type === TL4K_ALL_CAPS_MESSAGE && typeof data.enabled === "boolean") {
        setAllCaps(data.enabled);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  return [allCaps, setAllCaps];
}

/** Invia lo stato maiuscole a un iframe (usato da TechyWeb verso le app embedded). */
export function postAllCapsToWindow(target: Window | null | undefined, enabled: boolean): boolean {
  if (!target) return false;
  try {
    target.postMessage({ type: TL4K_ALL_CAPS_MESSAGE, enabled }, "*");
    return true;
  } catch (error) {
    console.warn("[postAllCapsToWindow]", error);
    return false;
  }
}

/** Aggiunge `allCaps=1`/`0` all'URL di un'app TL4K aperta da TechyWeb (stato iniziale). */
export function withAllCapsUrlParam(appUrl: string, enabled: boolean): string {
  try {
    const url = new URL(appUrl);
    url.searchParams.set(ALL_CAPS_URL_PARAM, enabled ? "1" : "0");
    return url.toString();
  } catch {
    const sep = appUrl.includes("?") ? "&" : "?";
    return `${appUrl}${sep}${ALL_CAPS_URL_PARAM}=${enabled ? "1" : "0"}`;
  }
}

export default useEmbeddedAllCaps;
